import { useRef } from "react";
import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, IconButton } from "@mui/material";
import { Close } from "@mui/icons-material";
import { BusyButton } from "@gui/widgets/BusyButton";
import type { AppDialogProps } from "./AppDialog.types";
import { useAppDialogViewModel } from "./AppDialog.vm";

const TITLE_SX = { flex: 1, minWidth: 0, px: 2 } as const;

/**
 * The app's dialog shell (docs/design-system.md §4, "Dialogs and confirmations"): the title, the
 * body, then Cancel (`text`) and the one `contained` primary action. On a phone a form or wide
 * dialog is full-screen, with a header row of ✕, title and primary action.
 */
export function AppDialog(props: AppDialogProps) {
	const primaryRef = useRef<HTMLButtonElement>(null);
	const cancelRef = useRef<HTMLButtonElement>(null);
	const vm = useAppDialogViewModel(props, { primary: primaryRef, cancel: cancelRef });

	const closeButton = vm.showHeaderClose && (
		<IconButton aria-label="Close" onClick={vm.handleClose} disabled={vm.closeDisabled}>
			<Close />
		</IconButton>
	);
	const primary = props.primaryAction && (
		<BusyButton
			variant="contained"
			size={vm.primaryInHeader ? "small" : undefined}
			color={props.primaryAction.color ?? "primary"}
			onClick={props.primaryAction.onClick}
			type={props.primaryAction.type}
			form={props.primaryAction.form}
			disabled={props.primaryAction.disabled}
			busy={props.primaryAction.busy}
			busyLabel={props.primaryAction.busyLabel}
			startIcon={props.primaryAction.icon}
			ref={primaryRef}
		>
			{props.primaryAction.label}
		</BusyButton>
	);

	return (
		<Dialog
			open={props.open}
			onClose={vm.handleClose}
			maxWidth={vm.maxWidth}
			fullWidth
			fullScreen={vm.fullScreen}
			className={props.className}
			data-testid={props["data-testid"]}
			disableScrollLock={props.disableScrollLock}
			aria-describedby={props["aria-describedby"]}
			slotProps={{
				paper: { sx: vm.paperSx },
				transition: { onEnter: props.onEnter, onEntering: vm.handleEntering, onExited: props.onExited },
			}}
		>
			{/* The title element holds the title and nothing else: MUI names the dialog by its whole text,
			    so the buttons sit beside it in the same row. */}
			<Box sx={{ display: "flex", alignItems: "center", gap: 1, px: 1 }}>
				{vm.headerCloseFirst && closeButton}
				<DialogTitle
					sx={vm.fullScreen ? { typography: "subtitle1", fontWeight: 500, flex: 1, minWidth: 0, px: 1 } : TITLE_SX}
				>
					{props.title}
				</DialogTitle>
				{props.headerActions}
				{vm.primaryInHeader && primary}
				{!vm.headerCloseFirst && closeButton}
			</Box>
			{props.children != null && (
				<DialogContent dividers={props.dividers} sx={vm.bodySx}>
					{props.children}
				</DialogContent>
			)}
			{vm.showFooter && (
				<DialogActions>
					{props.secondaryActions}
					{vm.showCancel && (
						<Button ref={cancelRef} onClick={vm.handleClose} disabled={vm.closeDisabled}>
							{vm.cancelLabel}
						</Button>
					)}
					{!vm.primaryInHeader && primary}
				</DialogActions>
			)}
		</Dialog>
	);
}
